/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import { FontWeights, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import {
  bodyContainerStackStyles,
  bodyContainerStackTokens,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { REASON_FOR_FLAG_DROPDOWN_OPTIONS } from "Constants/ApplicationConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { getFlagApplicationStatus } from "Dataflows/Application/ApplicationSelector";
import { fetchApplicationFlagsBySelectedApplicationId } from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getApplicationFlagsBySelectedApplicationId,
  getApplicationFlagsBySelectedApplicationIdErrorMessage,
  getIsLoadingApplicationFlagsBySelectedApplicationId,
  getSelectedUserProfileApplicationId,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { formatDate } from "Utility/FormatHelper";

const ApplicationFlags = (): ReactElement => {
  const dispatch = useDispatch();
  const isLoadingFlags = useSelector(getIsLoadingApplicationFlagsBySelectedApplicationId);
  const applicationFlagsErrorMessage = useSelector(
    getApplicationFlagsBySelectedApplicationIdErrorMessage
  );
  const flags = useSelector(getApplicationFlagsBySelectedApplicationId);
  const applicationFormId = useSelector(getSelectedUserProfileApplicationId);
  const flagApplicationStatus = useSelector(getFlagApplicationStatus);

  useEffect(() => {
    if (applicationFormId) {
      dispatch(fetchApplicationFlagsBySelectedApplicationId(applicationFormId));
    }
  }, []);

  useEffect(() => {
    if (applicationFormId) {
      dispatch(fetchApplicationFlagsBySelectedApplicationId(applicationFormId));
    }
  }, [flagApplicationStatus == OperationStatus.Succeeded]);

  if (isLoadingFlags) {
    return <Spinner size={SpinnerSize.large} />;
  }

  if (applicationFlagsErrorMessage) {
    return <Text>{applicationFlagsErrorMessage}</Text>;
  }

  /**
   * This function will find reason text from constant
   * @param flagKey input string from api response
   * @returns text for reason
   */
  function getFlagReasonText(flagKey: any) {
    const flagReason = REASON_FOR_FLAG_DROPDOWN_OPTIONS.find((item: any) => item.key == flagKey);
    const reasonText = flagReason && flagReason.text ? flagReason.text : "";
    return reasonText;
  }

  return (
    <Stack>
      {!!flags && flags?.length > 0 ? (
        flags.map((flag) => (
          <ContainerWrapper key={`open-flag-${flags.indexOf(flag)}`}>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>{`Open Flag ${flags.indexOf(flag) + 1}`}</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <FlexCol>
                <FlexRow style={{ marginBottom: "1rem" }}>
                  <FlexCol style={{ width: "50%" }}>
                    <Text styles={{ root: { fontWeight: FontWeights.semibold } }}>
                      Submitted By
                    </Text>
                    <Text>{flag.createdBy}</Text>
                  </FlexCol>
                  <FlexCol>
                    <Text styles={{ root: { fontWeight: FontWeights.semibold } }}>
                      Submitted On
                    </Text>
                    <Text>{formatDate(flag.dateCreated)}</Text>
                  </FlexCol>
                </FlexRow>
                <FlexRow style={{ marginBottom: "1rem" }}>
                  <FlexCol style={{ width: "50%" }}>
                    <Text styles={{ root: { fontWeight: FontWeights.semibold } }}>
                      Justification
                    </Text>
                    <Text>{flag.justificationText}</Text>
                  </FlexCol>
                  <FlexCol>
                    <Text styles={{ root: { fontWeight: FontWeights.semibold } }}>
                      Reason for flag
                    </Text>
                    {flag.flagReasonId ? <Text>{getFlagReasonText(flag.flagReasonId)}</Text> : null}
                  </FlexCol>
                </FlexRow>
              </FlexCol>
            </Stack>
          </ContainerWrapper>
        ))
      ) : (
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          <Text>There are no flags on this application.</Text>
        </Stack>
      )}
    </Stack>
  );
};

export default ApplicationFlags;
